import { useQuery } from "@tanstack/react-query";
import { BellRing, CalendarCheck, ChevronRight, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { api } from "@/lib/api-client";
import { useAppStore } from "@/store/app-store";
import { usePropertyAccess } from "@/lib/property-access";
import { currency, shortDate } from "@/lib/format";
import { statusClass, statusLabel } from "@/lib/status";
import type { Booking } from "@/types/domain";
import { LoadingGrid, StateView } from "@/components/ui/StateView";
import { Countdown } from "@/components/ui/Countdown";
import { DashboardTasks } from "./DashboardTasks";

type LiveRoomStatus = "OCCUPIED" | "RESERVED" | "AVAILABLE" | "MAINTENANCE";
type DashboardBooking = Pick<Booking, "id" | "bookingRef" | "guestName" | "checkInDate" | "checkOutDate" | "totalAmount" | "bookingStatus"> & {
  room?: { name: string } | null;
  roomType?: { name: string } | null;
};
// GET /dashboard - worked out on the server from the bookings (who is checked in / due tonight), in the property's timezone.
type DashboardData = {
  today: string;
  rooms: { id: string; name: string; roomNumber?: string | null; status: LiveRoomStatus; isClean: boolean }[];
  roomCounts: { total: number; occupied: number; reserved: number; available: number; maintenance: number };
  occupancyPercent: number;
  pending: { count: number; nextDeadline: string | null };
  arrivals: { count: number; checkedIn: number };
  departures: { count: number; overdue: number };
  inHouse: { bookings: number; guests: number };
  attentionRooms: { id: string; name: string; reason: "MAINTENANCE" | "NEEDS_CLEANING" }[];
  month: { bookedRevenue: number; bookings: number; collected: number };
  recent: DashboardBooking[];
};

const PULSE_LABEL: Record<LiveRoomStatus, string> = { OCCUPIED: "Occupied", RESERVED: "Arriving", AVAILABLE: "Free", MAINTENANCE: "Out of order" };

const nights = (booking: DashboardBooking) => Math.max(1, Math.round((new Date(booking.checkOutDate).getTime() - new Date(booking.checkInDate).getTime()) / 86_400_000));

const stayRange = (booking: DashboardBooking) => `${shortDate(booking.checkInDate)} → ${shortDate(booking.checkOutDate)} · ${nights(booking)}n`;

export function PropertyDashboardPage() {
  const navigate = useNavigate();
  const propertyId = useAppStore((state) => state.activeProperty!.id);
  const access = usePropertyAccess();
  // Under the "bookings" key so every booking change (socket events, mutations) refreshes it too.
  const dashboard = useQuery({ queryKey: ["bookings", propertyId, "dashboard"], queryFn: () => api<DashboardData>(`/dashboard?propertyId=${propertyId}`), refetchInterval: 60_000 });

  const now = new Date();
  const todayLabel = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short", year: "numeric" });

  if (dashboard.isLoading) return <main className="page dashboard"><LoadingGrid /></main>;
  if (dashboard.isError || !dashboard.data) {
    return <main className="page dashboard"><StateView title="We couldn't load your dashboard" message="Check your internet connection and try again." action={() => void dashboard.refetch()} /></main>;
  }
  const data = dashboard.data;
  const nextDeadline = data.pending.nextDeadline ? new Date(data.pending.nextDeadline).getTime() : 0;
  const firstAttention = data.attentionRooms[0];

  return (
    <main className="page dashboard">
      <header className="page-head">
        <div>
          <h1>Today</h1>
          <p className="page-head__meta">{todayLabel}{data.roomCounts.total ? ` · ${data.roomCounts.total} rooms` : ""}</p>
        </div>
        {access.can("BOOKINGS_MANAGE") && <button type="button" className="button button--primary page-head__action" onClick={() => navigate("/inventory")}><Plus size={18} /> New booking</button>}
      </header>

      <section aria-labelledby="needs-action" className="dash-section">
        <h2 id="needs-action" className="section-label">Needs action</h2>
        <div className="action-grid">
          <Link to="/bookings?status=PENDING" className={clsx("action-card action-card--requests", data.pending.count > 0 && "is-urgent")}>
            <span className="action-card__icon" aria-hidden="true"><BellRing size={20} /></span>
            <span className="action-card__body">
              <small>Pending requests</small>
              <strong>{data.pending.count ? `${data.pending.count} booking request${data.pending.count === 1 ? "" : "s"}` : "No pending requests"}</strong>
              {nextDeadline ? <span className="action-card__meta">Next expires in <Countdown until={nextDeadline} /></span> : <span className="action-card__meta">You're all caught up</span>}
            </span>
            {data.pending.count > 0 && <span className="action-card__cta">Review</span>}
          </Link>
          <Link to="/bookings?status=CONFIRMED" className="action-card">
            <span className="action-card__body"><small><span className="label-long">Arrivals today</span><span className="label-short">Arrivals</span></small><strong className="num">{data.arrivals.count}</strong><span className="action-card__meta">{data.arrivals.checkedIn} checked in</span></span>
          </Link>
          <Link to="/bookings?status=CHECKED_IN" className="action-card">
            <span className="action-card__body"><small><span className="label-long">Departures today</span><span className="label-short">Departures</span></small><strong className="num">{data.departures.count}</strong><span className="action-card__meta">{data.departures.overdue ? `${data.departures.overdue} overdue` : "Due to check out"}</span></span>
          </Link>
          <Link to="/rooms" className="action-card">
            <span className="action-card__body">
              <small><span className="label-long">Rooms needing attention</span><span className="label-short">Room issue</span></small>
              <strong className={clsx("num", data.attentionRooms.length > 0 && "text-danger")}>{data.attentionRooms.length}</strong>
              <span className="action-card__meta">{firstAttention ? `${firstAttention.name} · ${firstAttention.reason === "MAINTENANCE" ? "maintenance" : "needs cleaning"}` : "All rooms ready"}</span>
            </span>
          </Link>
        </div>
      </section>

      <DashboardTasks propertyId={propertyId} attentionRooms={data.attentionRooms} />

      <section aria-labelledby="this-month" className="dash-section">
        <h2 id="this-month" className="section-label">At a glance</h2>
        <div className="kpi-strip">
          <div title={`${data.month.bookings} booking(s) checking in this month · ${currency.format(data.month.collected)} collected`}><small>Revenue · this month</small><strong className="num">{currency.format(data.month.bookedRevenue)}</strong></div>
          <div title={`${data.roomCounts.occupied} of ${data.roomCounts.total - data.roomCounts.maintenance} sellable rooms occupied`}>
            <small>Occupancy · today</small><strong className="num">{data.occupancyPercent}%</strong>
            <span className="meter" aria-hidden="true"><i style={{ width: `${data.occupancyPercent}%` }} /></span>
          </div>
          <div className="kpi-strip__desktop"><small>Checked in · now</small><strong className="num">{data.inHouse.bookings}{data.inHouse.guests ? ` · ${data.inHouse.guests} guests` : ""}</strong></div>
          <div className="kpi-strip__desktop"><small>Available rooms · tonight</small><strong className="num">{data.roomCounts.available} of {data.roomCounts.total}</strong></div>
        </div>
      </section>

      <div className="dash-columns">
        <section className="card dash-recent" aria-labelledby="recent-bookings">
          <div className="card__head"><h2 id="recent-bookings">Recent bookings</h2><Link to="/bookings" className="text-link">See all <ChevronRight size={16} /></Link></div>
          {!data.recent.length ? (
            <div className="mini-empty"><CalendarCheck /><p>Your newest bookings will appear here.</p></div>
          ) : (
            <>
              <ul className="recent-list">
                {data.recent.map((booking) => (
                  <li key={booking.id}>
                    <Link to="/bookings">
                      <span className="recent-list__main"><strong>{booking.guestName}</strong><small className="num">{stayRange(booking)} · {currency.format(Number(booking.totalAmount))}</small></span>
                      <span className={statusClass(booking.bookingStatus)}>{statusLabel(booking.bookingStatus)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="table-wrap recent-table">
                <table className="data-table">
                  <thead><tr><th scope="col">Guest</th><th scope="col">Stay</th><th scope="col">Status</th><th scope="col" className="num-col">Amount</th></tr></thead>
                  <tbody>
                    {data.recent.map((booking) => (
                      <tr key={booking.id} onClick={() => navigate("/bookings")}>
                        <td><strong>{booking.guestName}</strong><small>{booking.room?.name ?? booking.roomType?.name ?? "Room"}</small></td>
                        <td className="num">{stayRange(booking)}</td>
                        <td><span className={statusClass(booking.bookingStatus)}>{statusLabel(booking.bookingStatus)}</span></td>
                        <td className="num-col num">{currency.format(Number(booking.totalAmount))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        <section className="card dash-pulse" aria-labelledby="room-pulse">
          <div className="card__head"><h2 id="room-pulse">Room pulse</h2><Link to="/rooms" className="text-link">Manage rooms <ChevronRight size={16} /></Link></div>
          <p className="page-head__meta">Each square is one room, as of tonight.</p>
          <div className="pulse-grid">
            {data.rooms.map((room) => <span key={room.id} className={`pulse-cell pulse-cell--${room.status.toLowerCase()}`} title={`${room.name} · ${PULSE_LABEL[room.status]}`} />)}
          </div>
          <ul className="pulse-legend">
            {(["OCCUPIED", "RESERVED", "AVAILABLE", "MAINTENANCE"] as const).map((status) => (
              <li key={status}><i className={`pulse-cell pulse-cell--${status.toLowerCase()}`} aria-hidden="true" />{PULSE_LABEL[status]} {data.rooms.filter((room) => room.status === status).length}</li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
}
